import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { AppStateApi, ApplicationApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import { parseApplicationSettings, parseApplicationSettingsValues } from "../../../utils/utils";
import AppStocksLinkingForm from "../AppStocksLinkingForm";
import GoPosBasicInfoForm from "./GoPosBasicInfoForm";
import GoPosDownloadingDataForm from "./GoPosDownloadingDataForm";
import GoPosSendingDataForm from "./GoPosSendingDataForm";

interface Props {
	appState: AppStateApi;
	handleUpdateApp: (app: ApplicationApi) => void;
}

const GoPosForm = ({ appState, handleUpdateApp }: Props) => {
	const form = useForm<ApplicationApi>({
		criteriaMode: "all",
		defaultValues: {
			...appState.app,
			settings: {
				...appState.app.settings,
				...parseApplicationSettingsValues(appState.app.settings),
				custom_fields:
					appState.app.settings.CUSTOM_FIELDS?.length > 0
						? [...appState.app.settings.CUSTOM_FIELDS.split(","), ""]
						: [""],
				PRODUCT_EXPORT_COST_TYPE: appState.app.settings.PRODUCT_EXPORT_COST_TYPE === "MAC",
				ORDER_EXPORT_COST_TYPE: appState.app.settings.ORDER_EXPORT_COST_TYPE === "FIFO",
			},
		},
	});
	const { formState, setError, handleSubmit, reset, watch } = form;
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();

	const onSubmit = handleSubmit(async (data: ApplicationApi) => {
		setLoading(true);
		try {
			const params: Record<string, any> = { include: "settings" };

			const settings = parseApplicationSettings(data.settings);
			settings.CUSTOM_FIELDS = settings.custom_fields
				.split(",")
				.filter((customField: string) => customField)
				.join(",");
			delete settings.custom_fields;

			if (settings.PRODUCT_EXPORT_COST_TYPE) {
				settings.PRODUCT_EXPORT_COST_TYPE = "MAC";
			} else {
				settings.PRODUCT_EXPORT_COST_TYPE = "DISABLED";
			}

			if (settings.ORDER_EXPORT_COST_TYPE) {
				settings.ORDER_EXPORT_COST_TYPE = "FIFO";
			} else {
				settings.ORDER_EXPORT_COST_TYPE = "DISABLED";
			}

			const newData = {
				name: data.name,
				id: appState.app.id,
				settings,
			};
			const res = await api.organization().updateApp(newData, params);
			handleUpdateApp(res);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			reset(watch());
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	if (!appState?.config) return <></>;

	return (
		<FormDirty formState={formState} className="app-form" onSubmit={onSubmit} loading={loading}>
			<GoPosBasicInfoForm form={form} provider={appState.app.provider} />
			<GoPosDownloadingDataForm form={form} config={appState.config} />
			<AppStocksLinkingForm form={form} config={appState.config} />
			<GoPosSendingDataForm form={form} appState={appState} />
		</FormDirty>
	);
};

export default GoPosForm;
